import ICU from "i18next-icu";
import IntlMessageFormat from "intl-messageformat";
import { localeDateFormatter } from "./intl-fallback";
import { localeNumberFormatter } from "./intl";

/** Keep the existing ICU plugin/cache/error policy, but give MK messages the
 * same standards-backed formatters as visible numbers/dates. i18next-icu's
 * stock parse path cannot accept IntlMessageFormat's custom formatters.
 */
type MessageValues = Parameters<IntlMessageFormat["format"]>[0];
type Parse = (
  source: string, values: Record<string, unknown>, language: string,
  namespace: string, key: string, info?: { resolved?: { res?: unknown } },
) => ReturnType<IntlMessageFormat["format"]>;

// i18next-icu publishes the constructor/plugin interface, but omits these
// existing runtime parse fields from its declarations. Keep that boundary
// explicit instead of changing the plugin's native path or global Intl.
type RuntimeICU = InstanceType<typeof ICU> & {
  parse: Parse;
  formats?: ConstructorParameters<typeof IntlMessageFormat>[2];
  options: {
    memoize: boolean;
    memoizeFallback: boolean;
    parseLngForICU: (language: string) => string;
    parseErrorHandler: (error: unknown, key: string, source: string, values: object) => string;
  };
  escapeVariableValues: (values: object) => MessageValues;
};

export const registryICU = (options: ConstructorParameters<typeof ICU>[0]): InstanceType<typeof ICU> => {
  const plugin = new ICU(options) as RuntimeICU;
  const messages = new Map<string, IntlMessageFormat>();
  const parse = plugin.parse.bind(plugin);
  const clearCache = plugin.clearCache.bind(plugin);
  plugin.clearCache = () => { clearCache(); messages.clear(); };
  const runtime = plugin;
  plugin.parse = (source, values, language, namespace, key, info) => {
    if (language.split("-")[0].toLowerCase() !== "mk") return parse(source, values, language, namespace, key, info);
    try {
      const tag = runtime.options.parseLngForICU(language);
      const cacheKey = JSON.stringify([tag, namespace, key, source]);
      let message = runtime.options.memoize ? messages.get(cacheKey) : undefined;
      if (!message) {
        message = new IntlMessageFormat(source, tag, runtime.formats, {
          ignoreTag: true,
          formatters: {
            getNumberFormat: (_locales, formatOptions) => localeNumberFormatter(tag, formatOptions),
            getDateTimeFormat: (_locales, formatOptions) => localeDateFormatter(tag, formatOptions),
            getPluralRules: (_locales, formatOptions) => new Intl.PluralRules(tag, formatOptions),
          },
        });
        if (runtime.options.memoize && (runtime.options.memoizeFallback || !info || info.resolved?.res)) messages.set(cacheKey, message);
      }
      return message.format(runtime.escapeVariableValues(values));
    } catch (error) {
      return runtime.options.parseErrorHandler(error, key, source, values);
    }
  };
  return plugin;
};
